/**
 * Shared helpers for the monthly supplier upload cycle.
 *
 * Barratt has no ERP feed: every month each supplier uploads a Supplier
 * Performance file (template 2) and a Quality file (template 4). These
 * helpers keep the customer-side tracker (Supplier Upload History), the
 * customer-side uploader (Data Upload) and the supplier-side page (Monthly
 * Uploads) agreeing on labels, template ids and period formatting.
 */
import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import {
  QUALITY_TEMPLATE_ID,
  SUPPLIER_PERFORMANCE_TEMPLATE_ID,
  type ImportTemplate,
  type SupplierUploadDatasetType,
  type SupplierUploadTracking,
} from '../../lib/api/barratt';

export const DATASET_LABELS: Record<SupplierUploadDatasetType, string> = {
  supplier_performance: 'Supplier Performance',
  quality: 'Quality',
};

export const TEMPLATE_ID_BY_DATASET: Record<SupplierUploadDatasetType, number> = {
  supplier_performance: SUPPLIER_PERFORMANCE_TEMPLATE_ID,
  quality: QUALITY_TEMPLATE_ID,
};

/** Display order of the two datasets inside a period. */
export const DATASET_ORDER: SupplierUploadDatasetType[] = ['supplier_performance', 'quality'];

/** Accepted by both monthly uploads; the templates are spreadsheets. */
export const UPLOAD_ACCEPT = '.csv,.xlsx,.xls';

type PeriodSource = Pick<SupplierUploadTracking, 'period' | 'period_label' | 'period_start'>;

/** 'YYYY-MM' for a tracking row — the serialised period when present, else from period_start. */
export function periodKey(row: PeriodSource): string {
  return row.period ?? row.period_start.slice(0, 7);
}

/** 'Aug 2026' */
export function periodLabelFor(row: PeriodSource): string {
  if (row.period_label) return row.period_label;
  return format(parseISO(row.period_start), 'MMM yyyy');
}

/** 'YYYY-MM' for today. */
export function currentPeriodKey(): string {
  return format(new Date(), 'yyyy-MM');
}

/** 'Aug 2026' from a 'YYYY-MM' key. */
export function periodKeyLabel(key: string): string {
  return format(parseISO(`${key}-01`), 'MMM yyyy');
}

export function datasetLabel(row: Pick<SupplierUploadTracking, 'dataset_type' | 'dataset_type_display'>): string {
  return row.dataset_type_display || DATASET_LABELS[row.dataset_type] || row.dataset_type;
}

/** '14 Sep 2026', or an em dash for nothing. */
export function formatDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  return format(parseISO(iso), 'd MMM yyyy');
}

/** '14 Sep 2026 09:41', or an em dash for nothing. */
export function formatDayTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return format(parseISO(iso), 'd MMM yyyy HH:mm');
}

/** '97.5%' or an em dash. */
export function formatOtif(pct: number | null | undefined): string {
  if (pct === null || pct === undefined) return '—';
  return `${Number(pct).toFixed(1)}%`;
}

export function formatCount(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString();
}

/**
 * Saves a template's header row as a CSV the supplier can fill in.
 *
 * Shared by the customer uploader (Data Upload) and the supplier one (Monthly
 * Uploads) so both hand out byte-identical headers — a template that differs
 * between the two sides is worse than none.
 *
 * @param template - the template whose columns become the header row
 * @returns the file name written, for the confirmation message
 */
export function downloadTemplateCsv(template: Pick<ImportTemplate, 'name' | 'columns'>): string {
  const fileName = `${template.name.replace(/\s+/g, '_')}_template.csv`;
  const blob = new Blob([template.columns.join(',')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return fileName;
}

/** Whole days since a due date passed. Zero or less means it is not late yet. */
export function daysOverdue(iso: string | null | undefined): number {
  if (!iso) return 0;
  return differenceInCalendarDays(new Date(), parseISO(iso));
}

/** '5 days overdue' / '3 months overdue', or null while the date still holds. */
export function overdueLabel(iso: string | null | undefined): string | null {
  const days = daysOverdue(iso);
  if (days <= 0) return null;
  if (days === 1) return '1 day overdue';
  if (days < 60) return `${days} days overdue`;
  const months = Math.floor(days / 30);
  return `${months} months overdue`;
}

/**
 * The row a person will find when they open the file.
 *
 * The importer numbers data rows from 1, so its "row 1" is the row under the
 * header — row 2 in Excel. Reporting the raw number sends people to the header
 * to look for a value that is one row further down.
 *
 * @param dataRow - `ImportException.row_number`; 0 means the header itself
 * @returns The spreadsheet row number, or null for a header-level problem
 */
export function spreadsheetRow(dataRow: number): number | null {
  return dataRow > 0 ? dataRow + 1 : null;
}

/**
 * True for an obligation the customer actually asked for.
 *
 * `useMyObligations` mints rows for months with no obligation on record so a
 * supplier can still send an earlier file; those carry negative ids. They are
 * an opportunity, never something the supplier is late on.
 */
export function isRequested(row: Pick<SupplierUploadTracking, 'id'>): boolean {
  return row.id > 0;
}
